import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";
import { ISSUE_OPERATOR_ACTION } from "../../issue-operator-action";

/**
 * The dump exists, arrives on time, and DOES NOT RESTORE — a third alarm, and
 * the only one of the three that is about the backup's contents.
 *
 * A THIRD CODE FOR THE SAME REASON THERE ARE ALREADY TWO. The other two
 * alarms are both blind to this failure by construction. `VIBE_APP_BACKUP_STALE`
 * lists the bucket, so a corrupt-but-recent-and-plausibly-sized archive
 * satisfies it completely. `VIBE_APP_BACKUP_JOB_FAILING` reads what the dump
 * job reported, and a dump job that exits 0 having written a broken archive
 * reports success honestly. Only loading the archive answers this, and folding
 * that verdict into either existing code would make an unrestorable backup
 * indistinguishable from a late one — different failures, different fixes, and
 * this is the one where the data is already gone.
 *
 * ABSENCE OF A DRILL IS NOT THIS ALARM. Drills are weekly and staggered by
 * app, so an app that has never been drilled has an UNPROVEN backup, not a
 * failed one — and "no backup at all" is already `VIBE_APP_BACKUP_STALE`'s
 * job. Raising on absence would fire across the entire fleet the day the
 * mechanism ships and teach everyone to ignore it before it ever caught
 * anything.
 *
 * A SKIPPED DRILL DOES NOT RAISE THIS, and that is deliberate. The drill
 * could not be ATTEMPTED — an unreadable master credential, no Vault session —
 * so the archive was never read and nothing about it is known. Raising a
 * critical issue that says the dump "did not verify" over a Secrets Manager
 * blip would be reporting a verdict nobody reached. A skip keeps whatever
 * alarm already exists (it certifies nothing) without raising a new one.
 *
 * The gap that leaves — drills that stop running permanently — is
 * `VIBE_APP_RESTORE_DRILL_STALE` below, and it is a separate code rather than
 * a widening of this one. This one means "we ran it and it did not verify";
 * that one means "nothing has verified it in too long". Still said plainly
 * here so this code is not mistaken for covering it.
 */
export const VIBE_APP_RESTORE_DRILL_FAILED = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "The most recent restore drill for this app's logical dump did not verify. The archive was fetched and put through pg_restore into a scratch database, and either it failed to load, or it loaded without any tables or readable rows — a dump that exists and is the right size but cannot be restored from. Raised per app, and separate from the staleness and job-health alarms because neither of those can see inside the archive. A drill that could not be attempted at all does NOT raise this — nothing was read, so there is no verdict to report — though it does keep any alarm already standing. Cleared once a drill verifies.",
  severity: "critical",
  remedy:
    "Run the drill now. Drills are weekly and staggered per app, so without this the next verdict on a failing archive is up to seven days away; the button overrides that rotation for today. It re-fetches the newest dump the control plane has seen land and puts it through pg_restore into a scratch database again. A second failure on the same archive means the archive itself is bad, and the next lever is the dump side — run the backup now (VIBE_APP_BACKUP_STALE carries that button) to produce a fresh one, then drill again. The request applies to today only and lapses at midnight UTC.",
  operatorAction: ISSUE_OPERATOR_ACTION.VIBE_RUN_APP_RESTORE_DRILL_NOW
} as const satisfies IssueDefinition;
